import { getIconFromTaxa } from 'Util/iconUtil.js'
import type { ProjectWithCounts } from '../../../Model/Project/ProjectWithCounts.js'
import { useNavigate } from 'react-router-dom'
import { UsersIcon, DocumentIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline'
import { useDispatch } from 'react-redux'
import { projectSlice } from '../../../Flux/Slice/Project/ProjectSlice.js'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import React from 'react'
import { DateWidget } from '../../Common/DateWidget.js'

const countLabel = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`

interface IProjectCompactProps {
  project: ProjectWithCounts
}

export function ProjectComponentCompact({ project }: IProjectCompactProps) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const currentUser = useGetAuthenticatedUser()

  const handleClick = () => {
    navigate(`/projects/${project.project.id}`)
  }

  const handleArchive = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent navigation
    if (window.confirm(`Are you sure you want to archive project "${project.project.name}"?`)) {
      dispatch(projectSlice.actions.deleteProjectRequest({ projectId: project.project.id }))
    }
  }

  return (
    <div
      data-testid={'project-container'}
      className="rounded-xl bg-slate-100 p-4 drop-shadow-sm relative hover:bg-slate-200 cursor-pointer transition-colors"
      onClick={handleClick}
    >
      <div className="flex gap-4 items-start">
        <div className="grow min-w-0">
          <h2 className="text-xl flex gap-x-2 items-baseline truncate">
            {project.project.targetTaxonomy && getIconFromTaxa(project.project.targetTaxonomy)}
            {project.project.name}
          </h2>
          {project.project.createdAt && (
            <span className="text-xs text-slate-500">
              created <DateWidget date={project.project.createdAt} />
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 text-sm text-slate-600 flex-shrink-0">
          <span className="flex items-center gap-1">
            <UsersIcon className="w-4 h-4" />
            {countLabel(project.participantCount, 'participant')}
          </span>
          <span className="flex items-center gap-1">
            <DocumentIcon className="w-4 h-4" />
            {countLabel(project.sampleCount, 'sample')}
          </span>
          {currentUser?.id === project.project.ownerUser && (
            <button
              data-testid={'delete-project'}
              className="btn btn-xs btn-outline btn-error flex items-center gap-1"
              onClick={handleArchive}
            >
              <ArchiveBoxIcon className="w-4 h-4" />
              Archive
            </button>
          )}
        </div>
      </div>
      {project.project.description && <p className="mt-2 truncate text-sm text-slate-700">{project.project.description}</p>}
    </div>
  )
}
